import { Drawer } from "@wags/ui/drawer.tsx";
import { toast } from "@wags/ui/sonner.tsx";
import type { ReactNode } from "react";
import { useState } from "react";
import {
    GOOGLE_SIGN_IN_START_FAILURE,
    useGoogleSignInStart,
} from "@/lib/core/auth-protocol";
import { withDefaultAuthRedirect } from "./auth-redirect";
import { useIsMobileViewport } from "@wags/tanstack-start/react";
import { readCurrentHash, restoreAuthHash } from "./browser/auth-hash";
import { MobileAuthDrawerContent } from "./mobile-auth-drawer-content";
import type { MobileAuthFormProps } from "./mobile-auth-form";
import type { ResponsiveSignInOptions } from "./responsive-auth.interface";
import { ResponsiveAuthContext } from "./browser/responsive-auth-context";

type MobileAuthRequest = {
    form: MobileAuthFormProps;
    returnHash: string;
};

export function ResponsiveAuthProvider({ children }: { children: ReactNode }) {
    const isMobile = useIsMobileViewport();
    const [mobileRequest, setMobileRequest] =
        useState<MobileAuthRequest | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const googleSignIn = useGoogleSignInStart({
        onFailure: () => toast.error(GOOGLE_SIGN_IN_START_FAILURE),
    });

    const open = (
        mode: "sign-in" | "sign-up",
        options: ResponsiveSignInOptions = {},
    ) => {
        const resolvedOptions = withDefaultAuthRedirect(options);
        if (isMobile) {
            setMobileRequest({
                form: { mode, options: resolvedOptions },
                returnHash: readCurrentHash(),
            });
            setMobileOpen(true);
            return;
        }
        void googleSignIn.start(resolvedOptions.fallbackRedirectUrl);
    };

    return (
        <ResponsiveAuthContext.Provider
            value={{
                openSignIn: (options) => open("sign-in", options),
                openSignUp: (options) => open("sign-up", options),
            }}
        >
            {children}
            {isMobile || mobileRequest ? (
                <Drawer
                    open={mobileOpen}
                    onOpenChange={(isOpen) => {
                        if (!isOpen && mobileRequest) {
                            restoreAuthHash(mobileRequest.returnHash);
                            setMobileOpen(false);
                        }
                    }}
                >
                    {mobileRequest ? (
                        <MobileAuthDrawerContent {...mobileRequest.form} />
                    ) : null}
                </Drawer>
            ) : null}
        </ResponsiveAuthContext.Provider>
    );
}
